import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { settlementsApi } from "@/api/settlements";
import type {
  ManualEntryRequest,
  OfwcaMapping,
  OrphanedAssignment,
  PolicyOverride,
  PolicyReassignment,
  SplitPolicyRequest,
} from "@/types/settlement";

// ============================
// Query Key Factory
// ============================

export interface SettlementsFilterParams {
  year?: number;
  month?: number;
  status?: string;
  agentId?: string;
  page?: number;
  pageSize?: number;
}

export interface AggregateFilterParams {
  year?: number;
  month?: number;
  status?: string;
  agentId?: string;
}

export interface AgentAggregateFilterParams {
  year?: number;
  month?: number;
  status?: string;
}

export const settlementKeys = {
  all: ["settlements"] as const,
  list: (params: SettlementsFilterParams) =>
    [...settlementKeys.all, "list", params] as const,
  aggregate: (params: AggregateFilterParams) =>
    [...settlementKeys.all, "aggregate", params] as const,
  agentSettlements: (agentId: string, year?: number) =>
    [...settlementKeys.all, "agent", agentId, year] as const,
  agentAggregate: (agentId: string, params: AgentAggregateFilterParams) =>
    [...settlementKeys.all, "agent", agentId, "aggregate", params] as const,
  agentPolicies: (agentId: string, year: number, month: number) =>
    [...settlementKeys.all, "policies", agentId, year, month] as const,
  imports: ["imports"] as const,
  insurers: ["insurers"] as const,
  monthChecklist: (year: number, month: number) =>
    [...settlementKeys.all, "month-checklist", year, month] as const,
};

// ============================
// Query Options (for router prefetch)
// ============================

export function agentSettlementsQueryOptions(agentId: string, year?: number) {
  return queryOptions({
    queryKey: settlementKeys.agentSettlements(agentId, year),
    queryFn: () =>
      settlementsApi.getAgentSettlements(agentId, {
        year,
        page: 1,
        pageSize: 20,
      }),
    staleTime: 30_000,
    enabled: !!agentId,
  });
}

export function agentPoliciesQueryOptions(
  agentId: string,
  year: number,
  month: number,
) {
  return queryOptions({
    queryKey: settlementKeys.agentPolicies(agentId, year, month),
    queryFn: () => settlementsApi.getAgentPolicies(agentId, year, month),
    staleTime: 30_000,
    enabled: !!agentId && !!year && !!month,
  });
}

export function allSettlementsQueryOptions(params: SettlementsFilterParams) {
  return queryOptions({
    queryKey: settlementKeys.list(params),
    queryFn: () => settlementsApi.getAllSettlements(params),
    staleTime: 30_000,
  });
}

export function insurersQueryOptions() {
  return queryOptions({
    queryKey: settlementKeys.insurers,
    queryFn: () => settlementsApi.getInsurers(),
    staleTime: 5 * 60_000,
  });
}

// ============================
// Queries
// ============================

export function useAllSettlements(
  params: SettlementsFilterParams,
  enabled = true,
) {
  return useQuery({ ...allSettlementsQueryOptions(params), enabled });
}

export function useSettlementsAggregate(
  params: AggregateFilterParams,
  enabled = true,
) {
  return useQuery({
    queryKey: settlementKeys.aggregate(params),
    queryFn: () => settlementsApi.getSettlementsAggregate(params),
    staleTime: 30_000,
    enabled,
  });
}

export function useAgentSettlementsAggregate(
  agentId: string,
  params: AgentAggregateFilterParams,
  enabled = true,
) {
  return useQuery({
    queryKey: settlementKeys.agentAggregate(agentId, params),
    queryFn: () => settlementsApi.getAgentSettlementsAggregate(agentId, params),
    staleTime: 30_000,
    enabled: enabled && !!agentId,
  });
}

export function useInsurers() {
  return useQuery(insurersQueryOptions());
}

export function useAgentSettlements(
  agentId: string,
  year?: number,
  enabled = true,
  page = 1,
  pageSize = 20,
  month?: number,
  status?: string,
) {
  return useQuery({
    queryKey: [
      ...settlementKeys.agentSettlements(agentId, year),
      month,
      status,
      page,
      pageSize,
    ] as const,
    queryFn: () =>
      settlementsApi.getAgentSettlements(agentId, {
        year,
        month,
        status,
        page,
        pageSize,
      }),
    staleTime: 30_000,
    enabled: enabled && !!agentId,
  });
}

export function useAgentPolicies(agentId: string, year: number, month: number) {
  return useQuery(agentPoliciesQueryOptions(agentId, year, month));
}

// ============================
// Mutations
// ============================

export function useSettlementApprovalPreview(id: string | null) {
  return useQuery({
    queryKey: [...settlementKeys.all, "approval-preview", id],
    queryFn: () => settlementsApi.getSettlementApprovalPreview(id!),
    enabled: !!id,
  });
}

export function useApproveSettlement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      policyOverrides,
    }: {
      id: string;
      policyOverrides?: PolicyReassignment[];
    }) => settlementsApi.approveSettlement(id, policyOverrides),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
}

export function useMarkSettlementPaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => settlementsApi.markSettlementPaid(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
}

export function useUnlockSettlement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      settlementsApi.unlockSettlement(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
}

export function useSettlementRevisions(id: string | null) {
  return useQuery({
    queryKey: [...settlementKeys.all, "revisions", id],
    queryFn: () => settlementsApi.getSettlementRevisions(id!),
    enabled: !!id,
  });
}

export function useExportSettlementExcel() {
  return useMutation({
    mutationFn: (id: string) => settlementsApi.exportSettlementExcel(id),
    onSuccess: ({ blob, fileName }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 100);
    },
  });
}

export function useDetectInsurer() {
  return useMutation({
    mutationFn: (file: File) => settlementsApi.detectInsurer(file),
  });
}

export function usePreviewImport() {
  return useMutation({
    mutationFn: ({
      file,
      insurerId,
      year,
      month,
    }: {
      file: File;
      insurerId: string;
      year: number;
      month: number;
    }) => settlementsApi.previewImport(file, insurerId, year, month),
  });
}

export function useConfirmImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      importId,
      mappings,
      policyOverrides,
      orphanedAssignments,
    }: {
      importId: string;
      mappings: OfwcaMapping[];
      policyOverrides?: PolicyOverride[];
      orphanedAssignments?: OrphanedAssignment[];
    }) =>
      settlementsApi.confirmImport(
        importId,
        mappings,
        policyOverrides,
        orphanedAssignments,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.imports });
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
}

// ============================
// Manual Entries
// ============================

export function useAddManualEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      settlementId,
      data,
    }: {
      settlementId: string;
      data: ManualEntryRequest;
    }) => settlementsApi.addManualEntry(settlementId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
}

export function useUpdateManualEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      settlementId,
      policyId,
      data,
    }: {
      settlementId: string;
      policyId: string;
      data: ManualEntryRequest;
    }) => settlementsApi.updateManualEntry(settlementId, policyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
}

export function useDeleteManualEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      settlementId,
      policyId,
    }: {
      settlementId: string;
      policyId: string;
    }) => settlementsApi.deleteManualEntry(settlementId, policyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
}

// ============================
// Commission Split (admin only)
// ============================

export function useSplitPolicyCommission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      settlementId,
      policyId,
      body,
    }: {
      settlementId: string;
      policyId: string;
      body: SplitPolicyRequest;
    }) => settlementsApi.splitPolicyCommission(settlementId, policyId, body),
    onSuccess: () => {
      // Splitting touches both settlements (primary + co) for the period — invalidate broadly.
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
}

export function useUnsplitPolicyCommission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      settlementId,
      policyId,
    }: {
      settlementId: string;
      policyId: string;
    }) => settlementsApi.unsplitPolicyCommission(settlementId, policyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
}

// ============================
// Import History
// ============================

export function importHistoryQueryOptions(
  page = 1,
  pageSize = 20,
  status?: string,
) {
  return queryOptions({
    queryKey: [
      ...settlementKeys.imports,
      "history",
      page,
      pageSize,
      status ?? "all",
    ] as const,
    queryFn: () => settlementsApi.getImportHistory({ page, pageSize, status }),
    staleTime: 30_000,
  });
}

export function useImportHistory(page = 1, pageSize = 20, status?: string) {
  return useQuery(importHistoryQueryOptions(page, pageSize, status));
}

export function useDeleteImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => settlementsApi.deleteImport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settlementKeys.imports });
      queryClient.invalidateQueries({ queryKey: settlementKeys.all });
    },
  });
}

export function useBatchPreviewImport() {
  return useMutation({
    mutationFn: ({
      file,
      insurerIds,
      year,
      month,
    }: {
      file: File;
      insurerIds: string[];
      year: number;
      month: number;
    }) => settlementsApi.batchPreviewImport(file, insurerIds, year, month),
  });
}

export function useCancelImport() {
  return useMutation({
    mutationFn: (importId: string) => settlementsApi.cancelImport(importId),
  });
}

// ============================
// Month checklist (administrators only)
// ============================

export function useMonthChecklist(year: number, month: number, enabled = true) {
  return useQuery({
    queryKey: settlementKeys.monthChecklist(year, month),
    queryFn: () => settlementsApi.getMonthChecklist(year, month),
    enabled,
  });
}

export function useConfirmNoCommission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      insurerId: string;
      year: number;
      month: number;
      note?: string;
    }) => settlementsApi.confirmNoCommission(data),
    onSuccess: (_data, { year, month }) => {
      queryClient.invalidateQueries({
        queryKey: settlementKeys.monthChecklist(year, month),
      });
    },
  });
}

export function useRevokeNoCommission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      insurerId,
      year,
      month,
    }: {
      insurerId: string;
      year: number;
      month: number;
    }) => settlementsApi.revokeNoCommission(insurerId, year, month),
    onSuccess: (_data, { year, month }) => {
      queryClient.invalidateQueries({
        queryKey: settlementKeys.monthChecklist(year, month),
      });
    },
  });
}
